import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { whiteboardsService } from '../api/whiteboards.service';
import { WHITEBOARD_KEYS } from './queryKeys';
import type { CreateWhiteboardPayload, RenameWhiteboardPayload } from '../types';

export function useCreateWhiteboard(boardId: string) {
  const queryClient = useQueryClient();
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';

  return useMutation({
    mutationFn: (data: CreateWhiteboardPayload) => whiteboardsService.create(boardId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: WHITEBOARD_KEYS.byBoard(activeOrganizationId, boardId),
      });
    },
  });
}

export function useRenameWhiteboard(boardId: string) {
  const queryClient = useQueryClient();
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';

  return useMutation({
    mutationFn: ({
      whiteboardId,
      payload,
    }: {
      whiteboardId: string;
      payload: RenameWhiteboardPayload;
    }) => whiteboardsService.rename(whiteboardId, payload),
    onSuccess: (_data, { whiteboardId }) => {
      queryClient.invalidateQueries({
        queryKey: WHITEBOARD_KEYS.byBoard(activeOrganizationId, boardId),
      });
      queryClient.invalidateQueries({
        queryKey: WHITEBOARD_KEYS.detail(activeOrganizationId, whiteboardId),
      });
    },
  });
}

export function useDeleteWhiteboard(boardId: string) {
  const queryClient = useQueryClient();
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';

  return useMutation({
    mutationFn: (whiteboardId: string) => whiteboardsService.delete(whiteboardId),
    onSuccess: (_data, whiteboardId) => {
      queryClient.invalidateQueries({
        queryKey: WHITEBOARD_KEYS.byBoard(activeOrganizationId, boardId),
      });
      // The board is gone server-side: drop its detail entry instead of refetching a 404.
      queryClient.removeQueries({
        queryKey: WHITEBOARD_KEYS.detail(activeOrganizationId, whiteboardId),
      });
    },
  });
}
